import { audio } from './engine';

export type Track = 'lobby' | 'age1' | 'age2' | 'age3';

interface TrackDef {
  root: number; // Hz de la nota base del dron
  scale: number[]; // semitonos sobre la raíz (dos octavas de punteos)
  bpm: number;
  density: number; // probabilidad de nota por corchea
  drum: boolean;
  brightness: number; // corte del filtro del dron
}

const TRACKS: Record<Track, TrackDef> = {
  lobby: { root: 73.42, scale: [0, 2, 3, 5, 7, 9, 10, 12, 14, 15, 17, 19], bpm: 62, density: 0.32, drum: false, brightness: 420 },
  age1: { root: 73.42, scale: [0, 2, 3, 5, 7, 9, 10, 12, 14, 15, 17, 19], bpm: 72, density: 0.4, drum: false, brightness: 520 },
  age2: { root: 82.41, scale: [0, 2, 4, 5, 7, 9, 10, 12, 14, 16, 17, 19], bpm: 80, density: 0.45, drum: true, brightness: 650 },
  age3: { root: 73.42, scale: [0, 1, 4, 5, 7, 8, 10, 12, 13, 16, 17, 19], bpm: 90, density: 0.5, drum: true, brightness: 780 },
};

const midiRatio = (semi: number) => Math.pow(2, semi / 12);

class MusicDirector {
  private want: Track | null = null;
  private current: Track | null = null;
  private bus: GainNode | null = null;
  private drone: OscillatorNode[] = [];
  private lfo: OscillatorNode | null = null;
  private timer: number | null = null;
  private nextTime = 0;
  private step = 0;
  private degree = 4;

  setTrack(t: Track | null): void {
    this.want = t;
    this.sync();
  }

  /** Se llama también cuando el audio se desbloquea. */
  sync(): void {
    if (!audio.ready) return;
    if (this.want === this.current) return;
    this.stop();
    if (this.want) this.start(this.want);
  }

  private start(name: Track): void {
    const ctx = audio.ctx!;
    const def = TRACKS[name];
    this.current = name;
    const bus = ctx.createGain();
    bus.gain.setValueAtTime(0.0001, ctx.currentTime);
    bus.gain.exponentialRampToValueAtTime(1, ctx.currentTime + 3);
    bus.connect(audio.music);
    this.bus = bus;

    // Dron: raíz + quinta, filtrado, con un LFO lento sobre el filtro.
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = def.brightness;
    filter.Q.value = 0.7;
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0.16;
    filter.connect(droneGain).connect(bus);
    this.lfo = ctx.createOscillator();
    this.lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = def.brightness * 0.35;
    this.lfo.connect(lfoGain).connect(filter.frequency);
    this.lfo.start();
    this.drone = [def.root, def.root * 1.5, def.root * 2.003].map((f, i) => {
      const o = ctx.createOscillator();
      o.type = i === 2 ? 'sine' : 'sawtooth';
      o.frequency.value = f;
      o.detune.value = (i - 1) * 6;
      o.connect(filter);
      o.start();
      return o;
    });

    this.nextTime = ctx.currentTime + 0.5;
    this.step = 0;
    this.timer = window.setInterval(() => this.schedule(def), 120);
  }

  private schedule(def: TrackDef): void {
    const ctx = audio.ctx;
    if (!ctx || !this.bus) return;
    const eighth = 60 / def.bpm / 2;
    while (this.nextTime < ctx.currentTime + 0.4) {
      const t = this.nextTime;
      if (def.drum && this.step % 8 === 0) this.drum(t, 0.5);
      if (def.drum && this.step % 8 === 5) this.drum(t, 0.25);
      const phrasePos = this.step % 32;
      const resting = phrasePos >= 28;
      if (!resting && Math.random() < def.density) {
        this.degree = Math.max(0, Math.min(def.scale.length - 1, this.degree + Math.round((Math.random() - 0.5) * 4)));
        const semi = def.scale[this.degree]!;
        this.pluck(t, def.root * 4 * midiRatio(semi), phrasePos % 8 === 0 ? 0.11 : 0.075);
        if (Math.random() < 0.15) this.pluck(t + eighth / 2, def.root * 4 * midiRatio(def.scale[Math.max(0, this.degree - 2)]!), 0.05);
      }
      this.nextTime += eighth;
      this.step++;
    }
  }

  /** Punteo tipo lira: triángulo + armónico con caída exponencial. */
  private pluck(t: number, f: number, peak: number): void {
    const ctx = audio.ctx!;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(f * 6, t);
    lp.frequency.exponentialRampToValueAtTime(f * 1.5, t + 1.2);
    g.connect(lp).connect(this.bus!);
    for (const [mult, type, amp] of [
      [1, 'triangle', 1],
      [2, 'sine', 0.35],
      [3, 'sine', 0.12],
    ] as const) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = f * mult;
      const og = ctx.createGain();
      og.gain.value = amp;
      o.connect(og).connect(g);
      o.start(t);
      o.stop(t + 2);
    }
  }

  private drum(t: number, peak: number): void {
    const ctx = audio.ctx!;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.3);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak * 0.5, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
    o.connect(g).connect(this.bus!);
    o.start(t);
    o.stop(t + 0.5);
  }

  stop(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    const ctx = audio.ctx;
    const bus = this.bus;
    const drone = this.drone;
    const lfo = this.lfo;
    if (ctx && bus) {
      bus.gain.cancelScheduledValues(ctx.currentTime);
      bus.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.6);
      window.setTimeout(() => {
        drone.forEach((o) => o.stop());
        lfo?.stop();
        bus.disconnect();
      }, 3000);
    }
    this.bus = null;
    this.drone = [];
    this.lfo = null;
    this.current = null;
  }
}

export const music = new MusicDirector();

if (typeof window !== 'undefined') {
  const kick = () => {
    audio.unlock();
    window.setTimeout(() => music.sync(), 50);
  };
  window.addEventListener('pointerdown', kick, { passive: true });
  window.addEventListener('keydown', kick);
}
